/**
 * Every country and territory on the world map, with its continent, for guesses anywhere on
 * Earth (not only in the countries the game plays). Keyed by ISO 3166-1 alpha-2 code.
 */
export const CONTINENTS = {
  AF: { name: 'Africa', emoji: '🌍' },
  AN: { name: 'Antarctica', emoji: '🧊' },
  AS: { name: 'Asia', emoji: '🌏' },
  EU: { name: 'Europe', emoji: '🌍' },
  NA: { name: 'North America', emoji: '🌎' },
  OC: { name: 'Oceania', emoji: '🌏' },
  SA: { name: 'South America', emoji: '🌎' },
} as const;
export type Continent = keyof typeof CONTINENTS;

/** [continent, English name] per country code. */
export const WORLD_COUNTRIES: Record<string, readonly [Continent, string]> = {
  AD: ['EU', 'Andorra'],
  AE: ['AS', 'United Arab Emirates'],
  AF: ['AS', 'Afghanistan'],
  AG: ['NA', 'Antigua and Barbuda'],
  AI: ['NA', 'Anguilla'],
  AL: ['EU', 'Albania'],
  AM: ['AS', 'Armenia'],
  AO: ['AF', 'Angola'],
  AQ: ['AN', 'Antarctica'],
  AR: ['SA', 'Argentina'],
  AS: ['OC', 'American Samoa'],
  AT: ['EU', 'Austria'],
  AU: ['OC', 'Australia'],
  AW: ['NA', 'Aruba'],
  AX: ['EU', 'Åland'],
  AZ: ['AS', 'Azerbaijan'],
  BA: ['EU', 'Bosnia and Herzegovina'],
  BB: ['NA', 'Barbados'],
  BD: ['AS', 'Bangladesh'],
  BE: ['EU', 'Belgium'],
  BF: ['AF', 'Burkina Faso'],
  BG: ['EU', 'Bulgaria'],
  BH: ['AS', 'Bahrain'],
  BI: ['AF', 'Burundi'],
  BJ: ['AF', 'Benin'],
  BL: ['NA', 'Saint Barthélemy'],
  BM: ['NA', 'Bermuda'],
  BN: ['AS', 'Brunei'],
  BO: ['SA', 'Bolivia'],
  BR: ['SA', 'Brazil'],
  BS: ['NA', 'Bahamas'],
  BT: ['AS', 'Bhutan'],
  BW: ['AF', 'Botswana'],
  BY: ['EU', 'Belarus'],
  BZ: ['NA', 'Belize'],
  CA: ['NA', 'Canada'],
  CD: ['AF', 'DR Congo'],
  CF: ['AF', 'Central African Republic'],
  CG: ['AF', 'Congo'],
  CH: ['EU', 'Switzerland'],
  CI: ['AF', "Côte d'Ivoire"],
  CK: ['OC', 'Cook Islands'],
  CL: ['SA', 'Chile'],
  CM: ['AF', 'Cameroon'],
  CN: ['AS', 'China'],
  CO: ['SA', 'Colombia'],
  CR: ['NA', 'Costa Rica'],
  CU: ['NA', 'Cuba'],
  CV: ['AF', 'Cabo Verde'],
  CW: ['NA', 'Curaçao'],
  CY: ['EU', 'Cyprus'],
  CZ: ['EU', 'Czechia'],
  DE: ['EU', 'Germany'],
  DJ: ['AF', 'Djibouti'],
  DK: ['EU', 'Denmark'],
  DM: ['NA', 'Dominica'],
  DO: ['NA', 'Dominican Republic'],
  DZ: ['AF', 'Algeria'],
  EC: ['SA', 'Ecuador'],
  EE: ['EU', 'Estonia'],
  EG: ['AF', 'Egypt'],
  EH: ['AF', 'Western Sahara'],
  ER: ['AF', 'Eritrea'],
  ES: ['EU', 'Spain'],
  ET: ['AF', 'Ethiopia'],
  FI: ['EU', 'Finland'],
  FJ: ['OC', 'Fiji'],
  FK: ['SA', 'Falkland Islands'],
  FM: ['OC', 'Micronesia'],
  FO: ['EU', 'Faroe Islands'],
  FR: ['EU', 'France'],
  GA: ['AF', 'Gabon'],
  GB: ['EU', 'United Kingdom'],
  GD: ['NA', 'Grenada'],
  GE: ['AS', 'Georgia'],
  GG: ['EU', 'Guernsey'],
  GH: ['AF', 'Ghana'],
  GL: ['NA', 'Greenland'],
  GM: ['AF', 'Gambia'],
  GN: ['AF', 'Guinea'],
  GQ: ['AF', 'Equatorial Guinea'],
  GR: ['EU', 'Greece'],
  GS: ['AN', 'South Georgia'],
  GT: ['NA', 'Guatemala'],
  GU: ['OC', 'Guam'],
  GW: ['AF', 'Guinea-Bissau'],
  GY: ['SA', 'Guyana'],
  HK: ['AS', 'Hong Kong'],
  HM: ['AN', 'Heard and McDonald Islands'],
  HN: ['NA', 'Honduras'],
  HR: ['EU', 'Croatia'],
  HT: ['NA', 'Haiti'],
  HU: ['EU', 'Hungary'],
  ID: ['AS', 'Indonesia'],
  IE: ['EU', 'Ireland'],
  IL: ['AS', 'Israel'],
  IM: ['EU', 'Isle of Man'],
  IN: ['AS', 'India'],
  IO: ['AS', 'British Indian Ocean Territory'],
  IQ: ['AS', 'Iraq'],
  IR: ['AS', 'Iran'],
  IS: ['EU', 'Iceland'],
  IT: ['EU', 'Italy'],
  JE: ['EU', 'Jersey'],
  JM: ['NA', 'Jamaica'],
  JO: ['AS', 'Jordan'],
  JP: ['AS', 'Japan'],
  KE: ['AF', 'Kenya'],
  KG: ['AS', 'Kyrgyzstan'],
  KH: ['AS', 'Cambodia'],
  KI: ['OC', 'Kiribati'],
  KM: ['AF', 'Comoros'],
  KN: ['NA', 'Saint Kitts and Nevis'],
  KP: ['AS', 'North Korea'],
  KR: ['AS', 'South Korea'],
  KW: ['AS', 'Kuwait'],
  KY: ['NA', 'Cayman Islands'],
  KZ: ['AS', 'Kazakhstan'],
  LA: ['AS', 'Laos'],
  LB: ['AS', 'Lebanon'],
  LC: ['NA', 'Saint Lucia'],
  LI: ['EU', 'Liechtenstein'],
  LK: ['AS', 'Sri Lanka'],
  LR: ['AF', 'Liberia'],
  LS: ['AF', 'Lesotho'],
  LT: ['EU', 'Lithuania'],
  LU: ['EU', 'Luxembourg'],
  LV: ['EU', 'Latvia'],
  LY: ['AF', 'Libya'],
  MA: ['AF', 'Morocco'],
  MC: ['EU', 'Monaco'],
  MD: ['EU', 'Moldova'],
  ME: ['EU', 'Montenegro'],
  MF: ['NA', 'Saint Martin'],
  MG: ['AF', 'Madagascar'],
  MH: ['OC', 'Marshall Islands'],
  MK: ['EU', 'North Macedonia'],
  ML: ['AF', 'Mali'],
  MM: ['AS', 'Myanmar'],
  MN: ['AS', 'Mongolia'],
  MO: ['AS', 'Macao'],
  MP: ['OC', 'Northern Mariana Islands'],
  MR: ['AF', 'Mauritania'],
  MS: ['NA', 'Montserrat'],
  MT: ['EU', 'Malta'],
  MU: ['AF', 'Mauritius'],
  MV: ['AS', 'Maldives'],
  MW: ['AF', 'Malawi'],
  MX: ['NA', 'Mexico'],
  MY: ['AS', 'Malaysia'],
  MZ: ['AF', 'Mozambique'],
  NA: ['AF', 'Namibia'],
  NC: ['OC', 'New Caledonia'],
  NE: ['AF', 'Niger'],
  NF: ['OC', 'Norfolk Island'],
  NG: ['AF', 'Nigeria'],
  NI: ['NA', 'Nicaragua'],
  NL: ['EU', 'Netherlands'],
  NO: ['EU', 'Norway'],
  NP: ['AS', 'Nepal'],
  NR: ['OC', 'Nauru'],
  NU: ['OC', 'Niue'],
  NZ: ['OC', 'New Zealand'],
  OM: ['AS', 'Oman'],
  PA: ['NA', 'Panama'],
  PE: ['SA', 'Peru'],
  PF: ['OC', 'French Polynesia'],
  PG: ['OC', 'Papua New Guinea'],
  PH: ['AS', 'Philippines'],
  PK: ['AS', 'Pakistan'],
  PL: ['EU', 'Poland'],
  PM: ['NA', 'Saint Pierre and Miquelon'],
  PN: ['OC', 'Pitcairn Islands'],
  PR: ['NA', 'Puerto Rico'],
  PS: ['AS', 'Palestine'],
  PT: ['EU', 'Portugal'],
  PW: ['OC', 'Palau'],
  PY: ['SA', 'Paraguay'],
  QA: ['AS', 'Qatar'],
  RE: ['AF', 'Réunion'],
  RO: ['EU', 'Romania'],
  RS: ['EU', 'Serbia'],
  RU: ['EU', 'Russia'],
  RW: ['AF', 'Rwanda'],
  SA: ['AS', 'Saudi Arabia'],
  SB: ['OC', 'Solomon Islands'],
  SC: ['AF', 'Seychelles'],
  SD: ['AF', 'Sudan'],
  SE: ['EU', 'Sweden'],
  SG: ['AS', 'Singapore'],
  SH: ['AF', 'Saint Helena'],
  SI: ['EU', 'Slovenia'],
  SK: ['EU', 'Slovakia'],
  SL: ['AF', 'Sierra Leone'],
  SM: ['EU', 'San Marino'],
  SN: ['AF', 'Senegal'],
  SO: ['AF', 'Somalia'],
  SR: ['SA', 'Suriname'],
  SS: ['AF', 'South Sudan'],
  ST: ['AF', 'São Tomé and Príncipe'],
  SV: ['NA', 'El Salvador'],
  SX: ['NA', 'Sint Maarten'],
  SY: ['AS', 'Syria'],
  SZ: ['AF', 'Eswatini'],
  TC: ['NA', 'Turks and Caicos Islands'],
  TD: ['AF', 'Chad'],
  TF: ['AN', 'French Southern Lands'],
  TG: ['AF', 'Togo'],
  TH: ['AS', 'Thailand'],
  TJ: ['AS', 'Tajikistan'],
  TL: ['AS', 'Timor-Leste'],
  TM: ['AS', 'Turkmenistan'],
  TN: ['AF', 'Tunisia'],
  TO: ['OC', 'Tonga'],
  TR: ['AS', 'Turkey'],
  TT: ['NA', 'Trinidad and Tobago'],
  TW: ['AS', 'Taiwan'],
  TZ: ['AF', 'Tanzania'],
  UA: ['EU', 'Ukraine'],
  UG: ['AF', 'Uganda'],
  US: ['NA', 'United States'],
  UY: ['SA', 'Uruguay'],
  UZ: ['AS', 'Uzbekistan'],
  VA: ['EU', 'Vatican City'],
  VC: ['NA', 'Saint Vincent and the Grenadines'],
  VE: ['SA', 'Venezuela'],
  VG: ['NA', 'British Virgin Islands'],
  VI: ['NA', 'U.S. Virgin Islands'],
  VN: ['AS', 'Vietnam'],
  VU: ['OC', 'Vanuatu'],
  WF: ['OC', 'Wallis and Futuna'],
  WS: ['OC', 'Samoa'],
  XK: ['EU', 'Kosovo'],
  YE: ['AS', 'Yemen'],
  ZA: ['AF', 'South Africa'],
  ZM: ['AF', 'Zambia'],
  ZW: ['AF', 'Zimbabwe'],
};

/**
 * The world map's features (Natural Earth, by numeric ISO id, or by name for the few without
 * one) and the country code each belongs to.
 */
export const WORLD_MAP_FEATURES: Record<string, string> = {
  '716': 'ZW',
  '894': 'ZM',
  '887': 'YE',
  '704': 'VN',
  '862': 'VE',
  '336': 'VA',
  '548': 'VU',
  '860': 'UZ',
  '858': 'UY',
  '583': 'FM',
  '584': 'MH',
  '580': 'MP',
  '850': 'VI',
  '316': 'GU',
  '016': 'AS',
  '630': 'PR',
  '840': 'US',
  '239': 'GS',
  '086': 'IO',
  '654': 'SH',
  '612': 'PN',
  '660': 'AI',
  '238': 'FK',
  '136': 'KY',
  '060': 'BM',
  '092': 'VG',
  '796': 'TC',
  '500': 'MS',
  '832': 'JE',
  '831': 'GG',
  '833': 'IM',
  '826': 'GB',
  '784': 'AE',
  '804': 'UA',
  '800': 'UG',
  '795': 'TM',
  '792': 'TR',
  '788': 'TN',
  '780': 'TT',
  '776': 'TO',
  '768': 'TG',
  '626': 'TL',
  '764': 'TH',
  '834': 'TZ',
  '762': 'TJ',
  '158': 'TW',
  '760': 'SY',
  '756': 'CH',
  '752': 'SE',
  '748': 'SZ',
  '740': 'SR',
  '728': 'SS',
  '729': 'SD',
  '144': 'LK',
  '724': 'ES',
  '410': 'KR',
  '710': 'ZA',
  '706': 'SO',
  'Somaliland': 'SO',
  '090': 'SB',
  '703': 'SK',
  '705': 'SI',
  '702': 'SG',
  '694': 'SL',
  '690': 'SC',
  '688': 'RS',
  '686': 'SN',
  '682': 'SA',
  '678': 'ST',
  '674': 'SM',
  '882': 'WS',
  '670': 'VC',
  '662': 'LC',
  '659': 'KN',
  '646': 'RW',
  '643': 'RU',
  '642': 'RO',
  '634': 'QA',
  '620': 'PT',
  '616': 'PL',
  '608': 'PH',
  '604': 'PE',
  '600': 'PY',
  '598': 'PG',
  '591': 'PA',
  '585': 'PW',
  '586': 'PK',
  '512': 'OM',
  '578': 'NO',
  '408': 'KP',
  '566': 'NG',
  '562': 'NE',
  '558': 'NI',
  '554': 'NZ',
  '570': 'NU',
  '184': 'CK',
  '528': 'NL',
  '533': 'AW',
  '531': 'CW',
  '524': 'NP',
  '520': 'NR',
  '516': 'NA',
  '508': 'MZ',
  '504': 'MA',
  '732': 'EH',
  '499': 'ME',
  '496': 'MN',
  '498': 'MD',
  '492': 'MC',
  '484': 'MX',
  '480': 'MU',
  '478': 'MR',
  '470': 'MT',
  '466': 'ML',
  '462': 'MV',
  '458': 'MY',
  '454': 'MW',
  '450': 'MG',
  '807': 'MK',
  '442': 'LU',
  '440': 'LT',
  '438': 'LI',
  '434': 'LY',
  '430': 'LR',
  '426': 'LS',
  '422': 'LB',
  '428': 'LV',
  '418': 'LA',
  '417': 'KG',
  '414': 'KW',
  'Kosovo': 'XK',
  '296': 'KI',
  '404': 'KE',
  '398': 'KZ',
  '400': 'JO',
  '392': 'JP',
  '388': 'JM',
  '380': 'IT',
  '376': 'IL',
  '275': 'PS',
  '372': 'IE',
  '368': 'IQ',
  '364': 'IR',
  '360': 'ID',
  '356': 'IN',
  '352': 'IS',
  '348': 'HU',
  '340': 'HN',
  '332': 'HT',
  '328': 'GY',
  '624': 'GW',
  '324': 'GN',
  '320': 'GT',
  '308': 'GD',
  '300': 'GR',
  '288': 'GH',
  '276': 'DE',
  '268': 'GE',
  '270': 'GM',
  '266': 'GA',
  '250': 'FR',
  '666': 'PM',
  '876': 'WF',
  '663': 'MF',
  '652': 'BL',
  '258': 'PF',
  '540': 'NC',
  '260': 'TF',
  '248': 'AX',
  '246': 'FI',
  '242': 'FJ',
  '231': 'ET',
  '233': 'EE',
  '232': 'ER',
  '226': 'GQ',
  '222': 'SV',
  '818': 'EG',
  '218': 'EC',
  '214': 'DO',
  '212': 'DM',
  '262': 'DJ',
  '304': 'GL',
  '234': 'FO',
  '208': 'DK',
  '203': 'CZ',
  'N. Cyprus': 'CY',
  '196': 'CY',
  '192': 'CU',
  '191': 'HR',
  '384': 'CI',
  '188': 'CR',
  '180': 'CD',
  '178': 'CG',
  '174': 'KM',
  '170': 'CO',
  '156': 'CN',
  '446': 'MO',
  '344': 'HK',
  '152': 'CL',
  '148': 'TD',
  '140': 'CF',
  '132': 'CV',
  '124': 'CA',
  '120': 'CM',
  '116': 'KH',
  '104': 'MM',
  '108': 'BI',
  '854': 'BF',
  '100': 'BG',
  '096': 'BN',
  '076': 'BR',
  '072': 'BW',
  '070': 'BA',
  '068': 'BO',
  '064': 'BT',
  '204': 'BJ',
  '084': 'BZ',
  '056': 'BE',
  '112': 'BY',
  '052': 'BB',
  '050': 'BD',
  '048': 'BH',
  '044': 'BS',
  '031': 'AZ',
  '040': 'AT',
  '036': 'AU',
  'Indian Ocean Ter.': 'AU',
  '334': 'HM',
  '574': 'NF',
  '051': 'AM',
  '032': 'AR',
  '028': 'AG',
  '024': 'AO',
  '020': 'AD',
  '012': 'DZ',
  '008': 'AL',
  '004': 'AF',
  'Siachen Glacier': 'IN',
  '010': 'AQ',
  '534': 'SX',
};

export function continentOf(countryCode: string | null): Continent | null {
  return countryCode ? (WORLD_COUNTRIES[countryCode]?.[0] ?? null) : null;
}
